const FILTER_STATE_PREFIX = 'gstocky.filters'
const SORT_STATE_PREFIX = 'gstocky.sorting'

function isSortStateField(path) {
  return path.split('.').some(segment => segment.toLowerCase().includes('sort'))
}

function getPath(object, path) {
  return path.split('.').reduce((value, key) => value == null ? undefined : value[key], object)
}

function setPath(vm, path, value) {
  const keys = path.split('.')
  const property = keys.pop()
  const target = keys.reduce((object, key) => object == null ? undefined : object[key], vm)

  if (target != null && property) vm.$set(target, property, value)
}

function restoreDates(value) {
  if (Array.isArray(value)) return value.map(restoreDates)
  if (typeof value !== 'string') return value

  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date
}

function getConfig(vm) {
  const option = vm.$options.persistedFilterState
  return typeof option === 'function' ? option.call(vm) : option
}

export default {
  created() {
    const config = getConfig(this)
    if (!config || !config.key || !Array.isArray(config.fields)) return

    const businessId = this.$store?.getters?.BUSINESS?.id || 'default'
    const userId = this.$store?.getters?.USER?.id || 'default'
    const routeScope = [
      this.$route?.name,
      ...(config.routeParams || []).map(param => this.$route?.params?.[param]),
    ].filter(value => value != null).join('.')

    const storageKeyParts = [config.key, businessId, userId, routeScope]

    this._persistedFilterStateConfig = config
    this._persistedFilterStateKey = [FILTER_STATE_PREFIX, ...storageKeyParts]
      .filter(Boolean)
      .join('.')
    this._persistedSortStateKey = [SORT_STATE_PREFIX, ...storageKeyParts]
      .filter(Boolean)
      .join('.')
    this._persistedFilterStateHasRouteOverride = (config.routeQueryFields || [])
      .some(field => this.$route?.query?.[field] != null)

    const restoredValues = {}
    const filterFields = config.fields.filter(path => !isSortStateField(path))
    const sortFields = config.fields.filter(isSortStateField)

    try {
      const stored = JSON.parse(localStorage.getItem(this._persistedFilterStateKey) || 'null')
      if (stored && typeof stored === 'object' && stored.values && typeof stored.values === 'object') {
        filterFields.forEach(path => {
          if (Object.prototype.hasOwnProperty.call(stored.values, path)) restoredValues[path] = stored.values[path]
        })

        // Remove sorting left behind by older versions from persistent storage.
        const containedPersistentSort = sortFields.some(path => Object.prototype.hasOwnProperty.call(stored.values, path))
        if (containedPersistentSort) this._saveStateToStorage(localStorage, this._persistedFilterStateKey, filterFields, stored.values)
      }
    } catch (error) { /* localStorage may be unavailable or contain stale data */ }

    try {
      const stored = JSON.parse(sessionStorage.getItem(this._persistedSortStateKey) || 'null')
      if (stored && typeof stored === 'object' && stored.values && typeof stored.values === 'object') {
        sortFields.forEach(path => {
          if (Object.prototype.hasOwnProperty.call(stored.values, path)) restoredValues[path] = stored.values[path]
        })
      }
    } catch (error) { /* sessionStorage may be unavailable or contain stale data */ }

    if (!this._persistedFilterStateHasRouteOverride && Object.keys(restoredValues).length) {
      this._persistedFilterStateSnapshot = JSON.stringify(restoredValues)
      this._restorePersistedFilterStateValues(restoredValues)
    }

    this._persistedFilterStateUnwatch = config.fields.map(path => this.$watch(
      path,
      () => this._savePersistedFilterState(),
      { deep: true }
    ))
  },
  mounted() {
    if (!this._persistedFilterStateSnapshot) return

    this.$nextTick(() => {
      if (this._persistedFilterStateHasRouteOverride) return

      try {
        this._restorePersistedFilterStateValues(JSON.parse(this._persistedFilterStateSnapshot))
      } catch (error) { /* ignore a stale in-memory snapshot */ }
    })
  },
  beforeDestroy() {
    this._savePersistedFilterState()
    ;(this._persistedFilterStateUnwatch || []).forEach(unwatch => unwatch())
  },
  methods: {
    _restorePersistedFilterStateValues(values) {
      const config = this._persistedFilterStateConfig
      if (!config || !values || typeof values !== 'object') return

      config.fields.forEach(path => {
        if (!Object.prototype.hasOwnProperty.call(values, path)) return
        const value = (config.dateFields || []).includes(path)
          ? restoreDates(values[path])
          : values[path]
        setPath(this, path, value)
      })
    },
    _savePersistedFilterState() {
      const config = this._persistedFilterStateConfig
      if (!config || !this._persistedFilterStateKey) return

      try {
        this._saveStateToStorage(
          localStorage,
          this._persistedFilterStateKey,
          config.fields.filter(path => !isSortStateField(path))
        )
      } catch (error) { /* localStorage may be unavailable */ }

      try {
        this._saveStateToStorage(
          sessionStorage,
          this._persistedSortStateKey,
          config.fields.filter(isSortStateField)
        )
      } catch (error) { /* sessionStorage may be unavailable */ }
    },
    _saveStateToStorage(storage, key, fields, sourceValues) {
      if (!fields.length) {
        storage.removeItem(key)
        return
      }

      const values = fields.reduce((state, path) => {
        const value = sourceValues ? sourceValues[path] : getPath(this, path)
        if (value !== undefined) state[path] = value
        return state
      }, {})

      storage.setItem(key, JSON.stringify({
        version: 1,
        values,
      }))
    },
  },
}
